import type {ReactNode} from 'react';
import React, {useMemo} from 'react';
import type {GridProps} from '@mui/material';
import {
  Box,
  Grid,
  Icon as MuiIcon,
  Tooltip as MuiTooltip,
  useTheme,
} from '@mui/material';
import type {TextProps} from 'components/text/types';
import {IconButton} from 'components/button';
import {Icon} from 'components/icons';
import {FlexBox} from 'components/layout';
import {Text} from 'components/text';
import {useSceneSizeContext} from './context/SceneSizeContext';

type Props = GridProps & {
  title?: string;
  tooltipTitle?: ReactNode;
  textProps?: TextProps;
  children?: ReactNode;
};

export const SceneTitle: React.FC<Props> = ({
  title,
  tooltipTitle,
  children,
  textProps,
  ...gridProps
}) => {
  const {fullWidth, toggleFullWidth} = useSceneSizeContext();
  const {palette} = useTheme();

  const titleComponent = useMemo(() => {
    return title ? (
      <Box
        component={Grid}
        container
        sx={{
          flexDirection: 'row',
          justifyContent: 'space-between',
          alignItems: 'center',
          width: '100%',
        }}>
        <Grid
          {...gridProps}
          sx={{
            overflow: 'hidden',
          }}>
          <FlexBox left center horizontal>
            {toggleFullWidth ? (
              <IconButton
                icon={fullWidth ? 'ChevronRight' : 'ChevronLeft'}
                onClick={toggleFullWidth}
              />
            ) : null}
            <Text
              sx={
                textProps?.sx
                  ? {color: palette.text.primary, ...textProps.sx}
                  : {color: palette.text.primary}
              }
              component={textProps?.component}
              variant={textProps?.variant ?? 'h1'}
              noWrap>
              {title}
            </Text>
            {tooltipTitle ? (
              <FlexBox>
                <MuiTooltip title={tooltipTitle}>
                  <MuiIcon>
                    <Icon icon="InfoCircle" />
                  </MuiIcon>
                </MuiTooltip>
              </FlexBox>
            ) : null}
          </FlexBox>
        </Grid>
        {children}
      </Box>
    ) : null;
  }, [
    title,
    gridProps,
    toggleFullWidth,
    fullWidth,
    textProps,
    tooltipTitle,
    children,
    palette,
  ]);

  return (
    <Box
      component={Grid}
      container
      sx={{
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
        width: '100%',
      }}>
      <Grid size={{xs: 12}}>{titleComponent}</Grid>
    </Box>
  );
};
